import { Button, Clickable, Col, Divider, Icon, Row, Text } from 'components';
import { LogoutModal } from 'components/Header/components';
import { theme } from 'lib';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import {
  ChangePassword,
  EmailNotifications,
  LanguageAndCurrency,
  PrivacyAndSecurity,
  SettingItem,
  StripeConnect,
  Subscriptions
} from './components';

const LeftBar = styled.div`
  width: ${(props) => (props.isMobile ? '100vw' : '296px')};
  height: calc(100vh - 113px);
  padding: 40px 0 0;
  border-right: 1px solid ${theme.GREY_20};
  position: fixed;
`;

const Settings = (props) => {
  const [selected, setSelected] = useState(null),
    [logoutVisible, setLogoutVisible] = useState(false),
    user = useSelector((state) => state.user),
    { isMobile, darkMode, showGetApp } = useSelector((state) => state.appState),
    { t } = useTranslation(),
    dispatch = useDispatch(),
    navigate = useNavigate(),
    location = useLocation(),
    scrollRef = useRef(null);

  // Get the query parameter from the URL
  const queryParams = new URLSearchParams(location.search);
  const cta = queryParams.get('cta');

  useEffect(() => {
    if (cta && cta.toLowerCase() === 'subscription') {
      setSelected(2);
      if (!isMobile) {
        scrollRef.current.scrollTo({
          behavior: 'smooth',
          top: settingList[2].reference.current.offsetTop - 73
        });
      }
    }
  }, [cta, isMobile]);

  function handleGetTheApp() {
    dispatch({
      type: 'UPDATE_APP_STATE',
      payload: {
        showGetApp: !showGetApp
      }
    });
  }

  function handleLogoutVisible() {
    setLogoutVisible(!logoutVisible);
  }

  const settingList = [
    {
      name: t('editProfile.editProfile'),
      icon: 'squareProfile',
      subtitle: t('settings.onlyAvailableViaApp'),
      onClick: handleGetTheApp,
      hasArrow: true,
      dimensions: 18
    },
    {
      name: t('settings.privacyAndSecurity'),
      icon: 'privacy',
      component: PrivacyAndSecurity,
      reference: useRef(null),
      dimensions: 22
    },
    // {
    //   name: t('settings.subscriptions'),
    //   icon: 'factCheck',
    //   component: Subscriptions,
    //   reference: useRef(null),
    //   dimensions: 22
    // },
    {
      name: t('settings.billingSubscription'),
      icon: 'receipt',
      onClick: () => navigate('/billing'),
      hasArrow: true,
      dimensions: 22
    },
    // {
    //   name: t('settings.oneOnOneConnect'),
    //   icon: 'videoCamera',
    //   subtitle: t('settings.onlyAvailableViaApp'),
    //   onClick: handleGetTheApp,
    //   hasArrow: true,
    //   dimensions: 18
    // },
    {
      name: t('settings.emailNotifications'),
      icon: 'emailNotification',
      reference: useRef(null),
      component: EmailNotifications,
      hide: true
    },
    {
      name: t('settings.changePassword'),
      title: 'Password',
      icon: 'changePassword',
      reference: useRef(null),
      component: ChangePassword,
      dimensions: 22
    },
    {
      name: t('settings.languageAndCurrency'),
      icon: 'language',
      reference: useRef(null),
      component: LanguageAndCurrency,
      dimensions: 22
    },
    {
      name: t('settings.darkMode'),
      icon: 'palette',
      hasSwitch: true,
      hide: true,
      switchActivated: darkMode,
      onClick: () =>
        dispatch({
          type: 'UPDATE_APP_STATE',
          payload: {
            darkMode: !darkMode
          }
        })
    },
    {
      name: 'Stripe Connect',
      title: 'Stripe Connect',
      icon: 'changePassword',
      reference: useRef(null),
      component: StripeConnect,
      hide: user.type !== 'team',
      dimensions: 22
    },
    {
      name: 'Divider'
    },
    {
      name: t('common.logOut'),
      icon: 'logout',
      onClick: handleLogoutVisible,
      dimensions: 18
    }
  ],
    selectedComponent = settingList[selected];

  return (
    <Col wid="100%" centerAll start noFlex>
      <LogoutModal visible={logoutVisible} closeModal={handleLogoutVisible} navigate={navigate} />

      <Row noFlex wid={isMobile ? '100vw' : '936px'} start>
        <Row wid="297px">
          <LeftBar isMobile={isMobile}>
            {settingList.map((x, i) => {
              if (x.hide) return null;

              return (
                <SettingItem
                  {...x}
                  index={i}
                  setSelected={setSelected}
                  isMobile={isMobile}
                  scrollRef={scrollRef}
                  key={i}
                />
              );
            })}

            {user.admin && (
              <Row noFlex marg="16px 0 0 16px">
                <Button
                  textButton
                  text="Go to back office"
                  col="red"
                  bg="red"
                  onClick={() => navigate('/admin')}
                />
              </Row>
            )}
          </LeftBar>
        </Row>

        {isMobile ? (
          selected && (
            <Col
              start
              wid="100vw"
              ht="calc(100vh - 120px)"
              pos="absolute"
              left="0"
              top="57px"
              bg="#fff"
              style={{ overflowY: 'scroll' }}
            >
              <Col
                start
                noFlex
                pad={isMobile ? '16px 0 0 16px' : '16px 0 0 24px'}
                wid={isMobile ? 'calc(100% - 32px)' : 'calc(100% - 24px)'}
              >
                <Row start center>
                  <Clickable marg="0 16px 0 0" onClick={() => setSelected(null)}>
                    <Icon type="arrowLeft" dimensions={18} />
                  </Clickable>

                  <Text h2>{selectedComponent.title || selectedComponent.name}</Text>
                </Row>

                <selectedComponent.component {...selectedComponent} />
              </Col>
            </Col>
          )
        ) : (
          <Col start ht="calc(100vh - 73px)" ref={scrollRef} style={{ overflowY: 'scroll' }}>
            {settingList.map((x, i) => {
              if (!x.component || x.hide) return null;

              return (
                <Col pad="40px 0 0 24px" key={i} ref={x.reference} wid="calc(100% - 24px)">
                  <Text h2>{x.title || x.name}</Text>

                  <x.component {...x} />
                </Col>
              );
            })}

            <Col wid="100%" marg="500px 0 0">
              <Divider />
            </Col>
          </Col>
        )}
      </Row>
    </Col>
  );
};

export default Settings;

/*
  <Col marg='40px 0 0 24px'>
    <Text h2>Current plan</Text>

    <Col marg='16px 0 0' bg={theme.SECONDARY} pad='24px' wid='568px'>
      <Row wid='100%' between>
        <Col>
          <Text h4 col='#fff'>6 Pack</Text>

          <Text b2 col='#fff' marg='8px 0 0 6px'>• 6 Athlete subscriptions</Text>
          <Text b2 col='#fff' marg='0 0 0 6px'>
            • Unlimited content for 6 athletes
          </Text>
        </Col>

        <Button
          wid='138px'
          reverse
          bordered
          text='Upgrade plan'
        />
      </Row>
    </Col>
  </Col>
*/
